/**
 * The brand icon inside each music-service logo file.
 *
 * Every `<service>-color-{light,dark}.svg` in `assets/images/` is a lockup: the brand
 * icon, then the name, in a viewBox 50 units high with empty space above and below.
 * Drawn whole in a small box, a lockup is a thin strip (Spotify a tiny wordmark,
 * Audiomack a speck). A crop is the rectangle of the icon alone, in viewBox units,
 * with no empty space around it. A client draws the same file with this rectangle
 * as its viewBox (web) or clips it to the rectangle (iOS), so no new artwork is needed.
 *
 * `light` is the crop of `<service>-color-light.svg` (dark ink, for a light surface),
 * `dark` of `<service>-color-dark.svg` (light ink, for a dark surface). A few files
 * place the art one unit lower in one variant, so the two can differ.
 *
 * `isIcon: false` marks a service whose lockup has no separate icon (Amazon Music is
 * one word logo): the crop is then the whole word logo, still with no empty space.
 *
 * The values were measured from the files (the ink bounds, rendered at 10 px a unit).
 * Measure again when a file changes. The iOS twin is `ServiceIconCrop.swift`.
 */
export interface ServiceArtRect {
  x: number;
  y: number;
  width: number;
  height: number;
}

export interface ServiceIconCrop {
  light: ServiceArtRect;
  dark: ServiceArtRect;
  isIcon: boolean;
}

const same = (rect: ServiceArtRect, isIcon = true): ServiceIconCrop => ({ light: rect, dark: rect, isIcon });

/** Keyed by `MusicAPIIntegrationID`, the `integrationType` the API sends. */
export const serviceIconCrops: Partial<Record<string, ServiceIconCrop>> = {
  spotify: {
    light: { x: 0.2, y: 11, width: 27.1, height: 27 },
    dark: { x: 0.2, y: 12, width: 27.1, height: 27 },
    isIcon: true,
  },
  appleMusic: same({ x: 0, y: 15, width: 15.7, height: 20 }),
  deezer: same({ x: 0.2, y: 23, width: 15.1, height: 12 }),
  youtube: {
    light: { x: 0, y: 14, width: 31, height: 21.7 },
    dark: { x: 0, y: 14, width: 30.7, height: 21.4 },
    isIcon: true,
  },
  youtubeMusic: same({ x: 0, y: 12, width: 25.7, height: 25.7 }),
  amazonMusic: same({ x: 0, y: 16, width: 113, height: 23 }, false),
  tidal: {
    light: { x: 0, y: 18.7, width: 18.3, height: 12.6 },
    dark: { x: 0, y: 18.6, width: 18.3, height: 12.7 },
    isIcon: true,
  },
  soundCloud: {
    light: { x: 0, y: 6, width: 66, height: 29 },
    dark: { x: 0, y: 7, width: 66, height: 29 },
    isIcon: true,
  },
  boomplay: same({ x: 0, y: 10, width: 30, height: 30 }),
  audiomack: same({ x: 0, y: 17.6, width: 27.4, height: 18.4 }),
  audius: same({ x: 0, y: 12.8, width: 22.7, height: 23.4 }),
  qobuz: same({ x: 0, y: 12.2, width: 25.4, height: 25.5 }),
  // The headphones icon left of the word "napster". The light file draws it 2.2 units higher.
  napster: {
    light: { x: 0, y: 8, width: 34.6, height: 33 },
    dark: { x: 0, y: 10.2, width: 34.6, height: 32.7 },
    isIcon: true,
  },
};

export const serviceIconCrop = (integrationType: string): ServiceIconCrop | undefined =>
  serviceIconCrops[integrationType];

/**
 * The drawn size of a crop. An icon gets the same visual weight as every other icon:
 * its area is `weight` squared, so a flat icon (Tidal, Deezer) is wider and a tall
 * one narrower than a square one, and none of them looks larger. A word logo has no
 * weight rule: it fills the box. Every size stays inside `max`.
 */
export const serviceArtSize = (
  crop: ServiceArtRect,
  isIcon: boolean,
  weight: number,
  max: { width: number; height: number }
): { width: number; height: number } => {
  const fit = Math.min(max.width / crop.width, max.height / crop.height);
  const scale = isIcon ? Math.min(weight / Math.sqrt(crop.width * crop.height), fit) : fit;
  const round = (value: number) => Math.round(value * 10) / 10;
  return { width: round(crop.width * scale), height: round(crop.height * scale) };
};
